/* Prices are re-read from the saved theme and Shopify before opening checkout. */
(() => {
  'use strict';
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const endpoint = '/apps/pc-configurator?action=catalog-checkout';
  let busy = false;
  let reviewed = null;
  let requestId = null;
  const enabled = () => window.ElitePCS?.privatePricing === true || Boolean(document.querySelector('[data-catalog-price-refresh]'))
    || ['components', 'accessories', 'warranty'].some(key => Number(window.ElitePCS?.productProfit?.[key]) > 0);
  const formSignature = form => JSON.stringify(Array.from(new FormData(form)).filter(([key]) => key !== 'return_to'));
  function status(button, message, error = false) {
    const drawer = button.closest('[data-cart-drawer]');
    let target = drawer?.querySelector('[data-cart-status]') || button.parentElement.querySelector('[data-catalog-checkout-status]');
    if (!target) {
      target = document.createElement('p'); target.dataset.catalogCheckoutStatus = '';
      target.setAttribute('role', 'status'); target.setAttribute('aria-live', 'polite');
      button.after(target);
    }
    target.textContent = message;
    target.style.color = error ? '#b42318' : '';
  }
  async function json(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store',
        headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok && !data.reviewRequired) throw new Error(data.message || data.description || 'Your basket could not be checked. Please try again.');
      return data;
    } finally { clearTimeout(timer); }
  }
  const readQuote = body => json(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const validCheckout = value => {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:' || !['elitepcs.co.uk', 'www.elitepcs.co.uk', '8qg51w-zn.myshopify.com'].includes(url.hostname)) throw new Error('Checkout could not be opened.');
    return url.href;
  };
  async function checkout(button, addForm = null) {
    if (busy) return;
    if (button.dataset.catalogAddUncertain === 'true') { location.assign('/cart'); return; }
    if (window.Shopify?.designMode || window.ElitePCS?.pcPricePreview) {
      status(button, 'Save your theme settings and open the storefront to test checkout.');
      return;
    }
    busy = true;
    const disabled = button.disabled;
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    try {
      status(button, 'Confirming your basket and current prices…');
      if (addForm) {
        const body = new FormData(addForm); body.delete('return_to');
        button.dataset.catalogAddUncertain = 'true';
        await json('/cart/add.js', { method: 'POST', body });
        delete button.dataset.catalogAddUncertain;
        button.dataset.catalogAddedSignature = JSON.stringify(Array.from(body));
      }
      if (!enabled()) { location.assign('/checkout'); return; }
      const cart = await json('/cart.js');
      if (!cart.items?.length) throw new Error('Your basket is empty.');
      if (cart.currency !== 'GBP') throw new Error('Please select GBP before continuing to checkout.');
      const payload = { items: cart.items.map(line => ({ variantId: line.variant_id, quantity: line.quantity,
        properties: line.properties || {}, sellingPlan: line.selling_plan_allocation?.selling_plan?.id || null })),
        discountCodes: (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code) };
      const quote = await readQuote(payload);
      if (quote.nativeCheckout) { location.assign('/checkout'); return; }
      if (!quote.success || !quote.fingerprint || !Number.isFinite(quote.price?.amount)) throw new Error('The current basket price is unavailable.');
      const scope = button.closest('[data-cart-drawer]') || button.closest('section') || document;
      const shown = scope.querySelector('[data-catalog-cart-total]');
      const expected = Number(shown?.dataset.catalogCartTotal);
      const actual = Math.round(quote.price.amount * 100);
      const changed = !shown || !Number.isFinite(expected) || expected !== actual;
      if (changed && reviewed !== quote.fingerprint) {
        reviewed = quote.fingerprint; requestId = null;
        document.querySelectorAll('[data-catalog-cart-total]').forEach(node => {
          node.dataset.catalogCartTotal = String(actual); node.textContent = money.format(quote.price.amount);
        });
        status(button, `Your basket total is ${money.format(quote.price.amount)} before delivery. Select ${addForm || button.hasAttribute('data-catalog-buy-now') ? 'Buy now' : 'Checkout'} again to continue. Discounts and delivery options are confirmed at checkout.`);
        return;
      }
      if (reviewed !== quote.fingerprint) { reviewed = quote.fingerprint; requestId = null; }
      requestId ||= window.crypto?.randomUUID?.() || `catalog-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const result = await readQuote({ ...payload, checkout: true, checkoutRequestId: requestId, fingerprint: quote.fingerprint });
      if (result.reviewRequired) {
        reviewed = null; requestId = null;
        status(button, 'Your basket price changed. Select Checkout to review the latest total.'); return;
      }
      if (result.nativeCheckout) { location.assign('/checkout'); return; }
      if (!result.success || !result.checkoutUrl) throw new Error('Checkout could not be prepared. Please try again.');
      status(button, 'Opening secure checkout…');
      location.assign(validCheckout(result.checkoutUrl));
    } catch (error) {
      status(button, button.dataset.catalogAddUncertain === 'true'
        ? 'We could not confirm this item was added. Select Buy now to review your basket before trying again.'
        : error?.name === 'AbortError' ? 'The basket check took too long. Please try again.' : error.message || 'Checkout could not be opened.', true);
    } finally { busy = false; button.disabled = disabled; button.removeAttribute('aria-busy'); }
  }
  document.addEventListener('submit', event => {
    const button = event.submitter;
    if (!button || event.defaultPrevented) return;
    if (button.hasAttribute('data-catalog-buy-now')) {
      event.preventDefault(); event.stopImmediatePropagation();
      void checkout(button, button.dataset.catalogAddedSignature === formSignature(event.target) ? null : event.target); return;
    }
    if (button.name === 'checkout' && enabled()) {
      event.preventDefault(); event.stopImmediatePropagation(); void checkout(button);
    }
  }, true);
  document.addEventListener('click', event => {
    const checkoutButton = event.target.closest('[data-catalog-checkout]');
    if (checkoutButton && !event.defaultPrevented) {
      event.preventDefault(); void checkout(checkoutButton); return;
    }
    const link = event.target.closest('a[href]');
    if (!link || !enabled() || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || url.pathname !== '/checkout') return;
    event.preventDefault(); void checkout(link);
  }, true);

  // Theme HTML can precede a newly saved margin. Fetch prices independently of
  // that render, including when returning to an already-open product tab.
  const products = new Map();
  const profitKeys = ['components', 'accessories', 'gamingPc', 'pcComponents', 'pcAccessories', 'warranty'];
  const preview = () => window.Shopify?.designMode || window.ElitePCS?.pcPricePreview;
  function nativePayments(root, hidden) {
    root.querySelectorAll('.a31-dynamic-checkout, .a31-payment-terms').forEach(node => { node.hidden = hidden; });
  }
  function paintProductPrice(root, amount) {
    const pennies = Math.round(amount * 100);
    const summary = root.querySelector('.elite-product-purchase-summary__price');
    if (!summary) return;
    summary.querySelector('.elite-product-purchase-summary__current').textContent = money.format(amount);
    const monthly = money.format(Math.round(pennies / 3) / 100);
    root.querySelectorAll('.elite-product-purchase-summary__monthly').forEach(node => { node.textContent = `${monthly}/mo`; });
    root.querySelectorAll('.elite-product-payments--standard .a31-klarna-box em').forEach(node => { node.textContent = `${monthly} each`; });
    document.getElementById(root.dataset.catalogKlarnaModal)?.querySelectorAll('.elite-klarna-modal__option p strong')
      .forEach(node => { node.textContent = `${monthly} each`; });
    const select = root.querySelector('select[name="id"]');
    const option = select?.selectedOptions[0];
    if (option?.dataset.catalogVariantTitle) {
      option.textContent = `${option.dataset.catalogVariantTitle} - ${money.format(amount)}${option.disabled ? ' (Sold out)' : ''}`;
    }
    const compare = root.dataset.catalogCompareSource === 'compare_at'
      ? Number(option?.dataset.catalogCompareAt ?? root.dataset.catalogCompareAt) : 0;
    const showCompare = root.dataset.catalogShowCompare === 'true' && Number.isFinite(compare) && compare > pennies;
    let strike = summary.querySelector('.elite-product-purchase-summary__compare');
    if (showCompare && !strike) {
      strike = document.createElement('s'); strike.className = 'elite-product-purchase-summary__compare'; summary.prepend(strike);
    }
    if (strike) { strike.hidden = !showCompare; if (showCompare) strike.textContent = money.format(compare / 100); }
    root.dataset.catalogPriceAmount = String(pennies);
  }
  async function refreshProduct(root, force = false) {
    if (preview() || document.visibilityState === 'hidden' || !root.isConnected) return;
    const state = products.get(root);
    const id = Number(root.querySelector('form.elite-product-purchase [name="id"]')?.value);
    if (!state || !Number.isSafeInteger(id) || id <= 0) return;
    if (state.variantId === id && (state.pending || (!force && Date.now() - state.started < 3000))) return;
    state.variantId = id; state.started = Date.now(); state.pending = true;
    const sequence = ++state.sequence;
    root.dataset.catalogPriceState = 'loading';
    nativePayments(root, true);
    try {
      const data = await json('/apps/pc-configurator?action=catalog-prices', { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ variantId: id, quantity: 1 }] }) });
      if (sequence !== state.sequence || !root.isConnected) return;
      const variant = data.variants?.find(item => item.variantId === id);
      if (!data.success || data.currency !== 'GBP' || typeof variant?.amount !== 'number' || variant.amount < 0
        || !Number.isSafeInteger(Math.round(variant.amount * 100))
        || typeof data.nativeCheckout !== 'boolean') {
        throw new Error('The latest product price is unavailable.');
      }
      paintProductPrice(root, variant.amount);
      nativePayments(root, !data.nativeCheckout);
      root.dataset.catalogPriceState = 'ready';
    } catch {
      // Keep the server-rendered amount on a temporary failure. Checkout always
      // validates the current saved price before it can create an order.
      if (sequence === state.sequence) root.dataset.catalogPriceState = 'error';
    } finally { if (sequence === state.sequence) state.pending = false; }
  }
  function initializeProducts() {
    if (preview()) return;
    document.querySelectorAll('[data-catalog-price-refresh]').forEach(root => {
      if (products.has(root)) return;
      products.set(root, { sequence: 0, started: 0, variantId: null, pending: false });
      root.addEventListener('change', event => { if (event.target.matches('[name="id"]')) void refreshProduct(root, true); });
      void refreshProduct(root);
    });
  }
  function refreshProducts(force = false) {
    products.forEach((_state, root) => { if (root.isConnected) void refreshProduct(root, force); else products.delete(root); });
  }
  window.addEventListener('focus', () => refreshProducts());
  window.addEventListener('pageshow', event => { if (event.persisted) refreshProducts(true); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'hidden') refreshProducts(); });
  document.addEventListener('shopify:section:load', initializeProducts);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeProducts, { once: true });
  else initializeProducts();
})();
